import {
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ApiManager } from '../../managers';
import { Event, Model, ModelNameEnum } from '../../types';
import { AlertContext } from '../Alert';
import { EventSelectorProps } from './EventSelector.types';

function EventSelector({ onChange, selectedEventId }: EventSelectorProps) {
  const { showAlert } = useContext(AlertContext);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [events, setEvents] = useState<Event[]>([]);

  const fetchEventRecords = useCallback(async () => {
    try {
      const response = await ApiManager.SendRequest<{
        model: Model;
      }>({
        route: 'data',
        action: 'query',
        model: ModelNameEnum.event,
        params: {},
      });

      if (response.error) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        return;
      }

      setEvents(response.results);
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.emssage}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);

  const input = useMemo(
    () => (
      <OutlinedInput
        label="Event"
        endAdornment={
          isLoading ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : undefined
        }
      />
    ),
    [isLoading],
  );

  const onChangeWrapper = (e: any) => {
    const selectedEvent = events.find(({ _id }) => _id.toString() === e.target.value);
    onChange(selectedEvent);
  };

  useEffect(() => {
    fetchEventRecords();
  }, [fetchEventRecords]);

  return (
    <FormControl className="select-control">
      <InputLabel className="select-label" id="event-select-label">
        Event
      </InputLabel>
      <Select
        labelId="event-select-label"
        id="event-select"
        input={input}
        value={selectedEventId}
        onChange={onChangeWrapper}
        disabled={isLoading || !events.length}
        displayEmpty>
        {events.map((event, i) => (
          <MenuItem key={`event-${i}`} value={event._id.toString()}>
            {event.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default EventSelector;
